import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Calendar, Play } from 'lucide-react';
import { useTimecardReportFilters } from '../hooks/useTimecardReportFilters';
import TimecardReportFiltersPanel from '../components/TimecardReportFiltersPanel';
import type { ReportHistoryItem } from '../components/ReportHistory';
import {
  commonFiltersFromParameters,
  commonFiltersMatch,
} from '../utils/timecardReportFilters';
import { LEGACY_EARLY_LATE_NET_MINUTES_MIN } from '../utils/earlyLateReport';

function localYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export interface EarlyLateReportRequest {
  reportType: 'timecards-early-late';
  parameters: {
    fromDate: string;
    toDate: string;
    netMinutesMin: number;
    employeeIds: number[];
    scheduleIds: number[];
    positionIds: number[];
    statusFilter?: 'all' | 'approved' | 'unapproved';
    employeeLabels?: string[];
    scheduleLabels?: string[];
    positionLabels?: string[];
  };
}

function netMinutesMinForDuplicateCompare(r: ReportHistoryItem): number {
  const v = r.parameters?.netMinutesMin;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const s = r.earlyLateSnapshot?.netMinutesMin;
  if (typeof s === 'number' && !Number.isNaN(s)) return s;
  return LEGACY_EARLY_LATE_NET_MINUTES_MIN;
}

interface TimecardsEarlyLateReportProps {
  onGenerateReport: (request: EarlyLateReportRequest) => void;
  runHistory: ReportHistoryItem[];
}

const TimecardsEarlyLateReport: React.FC<TimecardsEarlyLateReportProps> = ({
  onGenerateReport,
  runHistory,
}) => {
  const [toDate, setToDate] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [netMinutesMin, setNetMinutesMin] = useState(0);
  const [duplicateConfirmOpen, setDuplicateConfirmOpen] = useState(false);

  const {
    statusFilter,
    setStatusFilter,
    selectedEmployeeIds,
    setSelectedEmployeeIds,
    selectedScheduleIds,
    setSelectedScheduleIds,
    selectedPositionIds,
    setSelectedPositionIds,
    employees,
    schedules,
    positions,
    loadingOptions,
    buildCommonFilters,
  } = useTimecardReportFilters();

  useEffect(() => {
    const t = new Date();
    setToDate(localYmd(t));
    const f = new Date(t);
    f.setDate(f.getDate() - 15);
    setFromDate(localYmd(f));
  }, []);

  const hasDuplicateRun = useCallback(() => {
    const currentCommon = buildCommonFilters();
    return runHistory.some((r) => {
      if (r.reportType !== 'timecards-early-late' || r.status !== 'completed') return false;
      return (
        r.parameters?.fromDate === fromDate &&
        r.parameters?.toDate === toDate &&
        netMinutesMinForDuplicateCompare(r) === netMinutesMin &&
        commonFiltersMatch(currentCommon, commonFiltersFromParameters(r.parameters))
      );
    });
  }, [runHistory, fromDate, toDate, netMinutesMin, buildCommonFilters]);

  const queueReport = useCallback(() => {
    onGenerateReport({
      reportType: 'timecards-early-late',
      parameters: {
        fromDate,
        toDate,
        netMinutesMin,
        ...buildCommonFilters(),
      },
    });
  }, [onGenerateReport, fromDate, toDate, netMinutesMin, buildCommonFilters]);

  const handleRunReport = () => {
    if (!fromDate || !toDate || fromDate > toDate) return;
    if (hasDuplicateRun()) {
      setDuplicateConfirmOpen(true);
      return;
    }
    queueReport();
  };

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-7xl">
      <div>
        <h2 className="text-xl font-bold text-neutral-900">Early / Late Report</h2>
        <p className="text-sm text-neutral-600 mt-1">
          Include only rows whose net late/early time (minutes) is greater than the value you set below. Use filters to
          narrow by status, schedule, position, or employee. Reports run in the background — track progress in Report
          History below.
        </p>
      </div>

      <div className="bg-white rounded-lg border border-neutral-200 p-4 space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              <Calendar className="inline h-4 w-4 mr-1 align-text-bottom" />
              From date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-3 py-2 border border-neutral-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              <Calendar className="inline h-4 w-4 mr-1 align-text-bottom" />
              To date
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-3 py-2 border border-neutral-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Net Time (Mins.)&nbsp;&gt;</label>
            <input
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              value={netMinutesMin}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  setNetMinutesMin(0);
                  return;
                }
                const n = parseInt(raw, 10);
                if (!Number.isNaN(n) && n >= 0) setNetMinutesMin(n);
              }}
              className="w-24 px-3 py-2 border border-neutral-300 rounded-md text-sm tabular-nums"
            />
          </div>
          <button
            type="button"
            onClick={handleRunReport}
            disabled={!fromDate || !toDate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            <Play className="h-4 w-4" />
            Generate Report
          </button>
        </div>

        <TimecardReportFiltersPanel
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          employees={employees}
          schedules={schedules}
          positions={positions}
          selectedEmployeeIds={selectedEmployeeIds}
          onEmployeeIdsChange={setSelectedEmployeeIds}
          selectedScheduleIds={selectedScheduleIds}
          onScheduleIdsChange={setSelectedScheduleIds}
          selectedPositionIds={selectedPositionIds}
          onPositionIdsChange={setSelectedPositionIds}
          loadingOptions={loadingOptions}
        />
      </div>

      {duplicateConfirmOpen && (
        <div
          className="fixed inset-0 z-[75] flex items-center justify-center p-4 bg-black/50 backdrop-blur-[1px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="early-late-dup-title"
          onClick={() => setDuplicateConfirmOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-neutral-200/90 max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-4 border-b border-neutral-100 bg-gradient-to-br from-amber-50/90 to-white">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-800 ring-1 ring-amber-200/60">
                  <AlertTriangle className="h-5 w-5" aria-hidden />
                </div>
                <div className="min-w-0">
                  <h3 id="early-late-dup-title" className="text-base font-semibold text-neutral-900">
                    Duplicate report run
                  </h3>
                  <p className="text-sm text-neutral-600 mt-1 leading-relaxed">
                    A run with the same filters is already in your history. Generate another entry anyway?
                  </p>
                </div>
              </div>
            </div>
            <div className="px-5 py-4 flex flex-wrap justify-end gap-2 border-t border-neutral-100 bg-white">
              <button
                type="button"
                onClick={() => setDuplicateConfirmOpen(false)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setDuplicateConfirmOpen(false);
                  queueReport();
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 shadow-sm"
              >
                Generate anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimecardsEarlyLateReport;
